import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import Footer from "../../components/footer/Footer";
import NavbarComponent from "../../components/navbar/Navbar";
import Layouts from "../../config/layouts/Layouts";
import HeroSection from "../../components/home/HeroSection";
import FeaturesSection from "../../components/home/FeaturesSection";
import TeamSection from "../../components/home/TeamSection";
import ProductFeaturesSection from "../../components/home/ProductFeaturesSection";
import LatestPostsSection from "../../components/home/LatestPostsSection";
import CtaSection from "../../components/home/CtaSection";
import EmmBot from "../../components/common/EmmBot";
import "../../components/home/home.css";

// El Home funciona como un "deck": la cámara no baja, cada sección
// entra y sale lateralmente en el mismo encuadre.
const SLIDES = [
    { key: "inicio", label: "Inicio", render: () => <HeroSection /> },
    { key: "servicios", label: "Servicios", render: () => <FeaturesSection /> },
    { key: "equipo", label: "Equipo", render: () => <TeamSection /> },
    { key: "videos-ia", label: "Videos IA", render: () => <ProductFeaturesSection /> },
    { key: "blog", label: "Blog", render: () => <LatestPostsSection /> },
    { key: "contacto", label: "Contacto", render: () => <CtaSection /> },
];

const HASH_TO_INDEX = {
    servicios: 1,
    equipo: 2,
    "videos-ia": 3,
    blog: 4,
    contacto: 5,
};

const WHEEL_THRESHOLD = 90;
const SWIPE_THRESHOLD = 60;
const TRANSITION_MS = 1050;

const slideVariants = {
    enter: (dir) => ({
        x: dir >= 0 ? "62vw" : "-62vw",
        opacity: 0,
        scale: 0.94,
    }),
    center: { x: 0, opacity: 1, scale: 1 },
    exit: (dir) => ({
        x: dir >= 0 ? "-62vw" : "62vw",
        opacity: 0,
        scale: 0.94,
    }),
};

function Home() {
    const { t } = useTranslation();
    const location = useLocation();
    const reduceMotion = useReducedMotion();

    const [[index, direction], setIndexState] = useState([0, 0]);
    const busyRef = useRef(false);
    const wheelAccumRef = useRef(0);
    const innerRef = useRef(null);
    const touchStartYRef = useRef(null);
    const lastInnerScrollRef = useRef(0);

    // Mientras el scroll interno esté "caliente" (incluida la inercia en
    // móvil), no se cambia de escena: así se puede leer hasta el final.
    const innerScrollSettled = () => Date.now() - lastInnerScrollRef.current > 400;
    const handleInnerScroll = () => {
        lastInnerScrollRef.current = Date.now();
        wheelAccumRef.current = 0;
    };

    // Ref del contenedor con scroll de la escena ACTIVA. Con AnimatePresence
    // conviven dos slides durante la transición y el que sale, al
    // desmontarse, pondría el ref en null (rompiendo la detección de borde):
    // por eso solo aceptamos nodos nuevos e ignoramos los null de desmontaje.
    const setInnerRef = useCallback((node) => {
        if (node) innerRef.current = node;
    }, []);

    const lockBriefly = useCallback(() => {
        busyRef.current = true;
        wheelGestureAtEdgeRef.current = false;
        setTimeout(() => {
            busyRef.current = false;
            wheelAccumRef.current = 0;
        }, TRANSITION_MS);
    }, []);

    const goToIndex = useCallback(
        (next) => {
            setIndexState(([cur]) => {
                if (next === cur || next < 0 || next >= SLIDES.length) {
                    return [cur, 0];
                }
                lockBriefly();
                return [next, next > cur ? 1 : -1];
            });
        },
        [lockBriefly],
    );

    const goStep = useCallback(
        (dir) => {
            if (busyRef.current) return;
            setIndexState(([cur]) => {
                const next = cur + dir;
                if (next < 0 || next >= SLIDES.length) return [cur, dir];
                lockBriefly();
                return [next, dir];
            });
        },
        [lockBriefly],
    );

    // Si la sección visible es más alta que la pantalla, primero se
    // recorre su scroll interno; el cambio de slide ocurre en los bordes.
    const innerAtEdge = (deltaDown) => {
        const inner = innerRef.current;
        if (!inner) return true;
        if (deltaDown) {
            return inner.scrollTop + inner.clientHeight >= inner.scrollHeight - 2;
        }
        return inner.scrollTop <= 2;
    };

    // Navegación por GESTOS: la tanda de rueda/swipe que te lleva hasta el
    // borde de una sección nunca cambia de escena (aunque sigas girando la
    // rueda o haya inercia). Solo un gesto NUEVO, iniciado estando ya en el
    // borde, pasa a la siguiente. Así siempre se puede leer todo.
    const lastWheelTimeRef = useRef(0);
    const wheelGestureAtEdgeRef = useRef(false);
    const touchEdgeRef = useRef({ down: false, up: false });

    const handleWheel = (e) => {
        if (busyRef.current) return;
        const now = Date.now();
        const isNewGesture = now - lastWheelTimeRef.current > 250;
        lastWheelTimeRef.current = now;
        if (isNewGesture) {
            wheelGestureAtEdgeRef.current =
                innerAtEdge(e.deltaY > 0) && innerScrollSettled();
            wheelAccumRef.current = 0;
        }
        if (!wheelGestureAtEdgeRef.current) return;
        wheelAccumRef.current += e.deltaY;
        if (wheelAccumRef.current > WHEEL_THRESHOLD) {
            wheelAccumRef.current = 0;
            goStep(1);
        } else if (wheelAccumRef.current < -WHEEL_THRESHOLD) {
            wheelAccumRef.current = 0;
            goStep(-1);
        }
    };

    const handleTouchStart = (e) => {
        touchStartYRef.current = e.touches[0].clientY;
        // El borde se evalúa al INICIO del gesto: un swipe que recorre
        // contenido no cambia de escena aunque termine en el borde.
        touchEdgeRef.current = {
            down: innerAtEdge(true) && innerScrollSettled(),
            up: innerAtEdge(false) && innerScrollSettled(),
        };
    };

    const handleTouchEnd = (e) => {
        if (busyRef.current || touchStartYRef.current == null) return;
        const dy = touchStartYRef.current - e.changedTouches[0].clientY;
        touchStartYRef.current = null;
        if (Math.abs(dy) < SWIPE_THRESHOLD) return;
        if (dy > 0 && !touchEdgeRef.current.down) return;
        if (dy < 0 && !touchEdgeRef.current.up) return;
        goStep(dy > 0 ? 1 : -1);
    };

    useEffect(() => {
        const onKey = (e) => {
            if (busyRef.current) return;
            const tag = (e.target.tagName || "").toLowerCase();
            if (tag === "input" || tag === "textarea" || e.target.isContentEditable) return;
            if (["ArrowDown", "PageDown", " "].includes(e.key)) {
                e.preventDefault();
                if (innerAtEdge(true)) {
                    goStep(1);
                } else {
                    innerRef.current?.scrollBy({ top: 140, behavior: "smooth" });
                }
            } else if (["ArrowUp", "PageUp"].includes(e.key)) {
                e.preventDefault();
                if (innerAtEdge(false)) {
                    goStep(-1);
                } else {
                    innerRef.current?.scrollBy({ top: -140, behavior: "smooth" });
                }
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [goStep]);

    // Los enlaces del navbar (/#servicios, /#contacto...) mueven la cámara;
    // "Home" (sin hash) vuelve a la primera escena.
    useEffect(() => {
        const id = (location.hash || "").replace(/^#/, "");
        if (!id) {
            goToIndex(0);
            return;
        }
        const slideIdx = HASH_TO_INDEX[id];
        if (slideIdx !== undefined) goToIndex(slideIdx);
    }, [location.hash, location.key, goToIndex]);

    return (
        <Layouts>
            <header className="shrink-0">
                <NavbarComponent />
            </header>

            <main
                className="deck"
                onWheel={handleWheel}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
            >
                <AnimatePresence initial={false} custom={direction} mode="popLayout">
                    <motion.div
                        key={index}
                        className="deck-slide"
                        custom={direction}
                        variants={slideVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={
                            reduceMotion
                                ? { duration: 0 }
                                : { duration: 0.85, ease: [0.22, 1, 0.36, 1] }
                        }
                    >
                        <div
                            className="deck-slide-inner"
                            ref={setInnerRef}
                            onScroll={handleInnerScroll}
                        >
                            {SLIDES[index].render()}
                        </div>
                    </motion.div>
                </AnimatePresence>

                <nav className="deck-dots" aria-label={t("home.sectionsAria")}>
                    {SLIDES.map((slide, i) => (
                        <button
                            key={slide.key}
                            type="button"
                            className={i === index ? "deck-dot deck-dot--active" : "deck-dot"}
                            aria-label={slide.label}
                            aria-current={i === index ? "true" : undefined}
                            title={slide.label}
                            onClick={() => goToIndex(i)}
                        />
                    ))}
                </nav>

                {index < SLIDES.length - 1 && (
                    <motion.div
                        className="deck-hint"
                        aria-hidden="true"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.2 }}
                    >
                        <motion.span
                            className="deck-hint-wheel"
                            animate={reduceMotion ? {} : { y: [0, 6, 0] }}
                            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                        />
                    </motion.div>
                )}

                <EmmBot onEngage={() => goToIndex(SLIDES.length - 1)} />
            </main>

            <Footer />
        </Layouts>
    );
}

export default Home;
